import type { FC } from 'react';
import React, { Fragment, useState, useCallback, useContext } from 'react';

import { Box } from '@atlaskit/primitives/compiled';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled';
import { HOME_RIGHT_PANEL_FORGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component';
import type { ForgeUIExtensionType } from '@confluence/forge-ui/entry-points/types';
import { FORGE_MODULE_HOMEPAGE_FEED } from '@confluence/forge-ui/entry-points/ForgeModuleType';
import {
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
} from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut';
import { HomepageFeedForgeApp } from '@confluence/forge-ui/entry-points/apps';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle';
import { LazyForgeUIExtensions } from '@confluence/forge-ui/entry-points/lazy';

import { CollapsibleSection } from '../CollapsibleSection';
import { HomeWidgetState } from '../__types__/HomeWidgetsQuery';
type CollapsibleForgeAppsProps = {
	forgeApps: ReadonlyArray<ForgeUIExtensionType>;
};

const CollapsibleForgeApps: FC<CollapsibleForgeAppsProps> = ({ forgeApps }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [appInitialStates, setAppInitialStates] = useState(new Map());

	const onInitialRender = useCallback(() => {
		experienceTracker.succeed({
			name: HOME_RIGHT_PANEL_FORGE_EXPERIENCE,
		});
	}, [experienceTracker]);

	return (
		<Fragment>
			{forgeApps.map((app: ForgeUIExtensionType) => (
				<ForgeKeyboardShortcutVisualizer key={app.id} module={app}>
					<CollapsibleSection
						sectionId={app.id}
						header={extensionTitle(app)}
						initialStateOverride={appInitialStates.get(app.id)}
					>
						<ExperienceStart name={HOME_RIGHT_PANEL_FORGE_EXPERIENCE} id={app.id} />
						<Box padding="space.100">
							<HomepageFeedForgeApp
								app={app}
								extensionData={{ type: FORGE_MODULE_HOMEPAGE_FEED }}
								onInitialRender={onInitialRender}
							/>
						</Box>
					</CollapsibleSection>
					<ForgeKeyboardShortcut
						module={app}
						action={() => {
							const oldValue = appInitialStates.get(app.id);
							const newValue =
								oldValue === HomeWidgetState.EXPANDED
									? HomeWidgetState.COLLAPSED
									: HomeWidgetState.EXPANDED;
							setAppInitialStates(new Map(appInitialStates.set(app.id, newValue)));
						}}
					/>
				</ForgeKeyboardShortcutVisualizer>
			))}
		</Fragment>
	);
};

export const ForgeApps: FC<{}> = () => {
	return (
		<LazyForgeUIExtensions
			moduleType={FORGE_MODULE_HOMEPAGE_FEED}
			render={(forgeModules, loading, error) => {
				if (loading) return null;

				if (error) {
					if (!isErrorMarkedAsHandled(error)) {
						return <ErrorDisplay error={error} />;
					}
					return null;
				}

				return (
					<ErrorBoundary attribution={Attribution.DISCO}>
						<CollapsibleForgeApps forgeApps={forgeModules} />
					</ErrorBoundary>
				);
			}}
		/>
	);
};
